import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { TrendingUp, ArrowRight } from "lucide-react";
import { getBySlug } from "@/data/calculators";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pulls top-clicked calculators from /api/tools/popular and renders them
// as a "Trending now" strip. Any visitor who has clicked into a calculator
// has already incremented its view count via /api/tools/view (fired by
// CalculatorShell's mount effect). Hides itself entirely if the API fails
// or returns nothing — never blocks the homepage.
export default function PopularTools() {
    const [items, setItems] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        axios
            .get(`${API}/tools/popular?limit=12`)
            .then((r) => {
                if (cancelled) return;
                const enriched = (r.data || [])
                    .map((row) => {
                        const meta = getBySlug(row.slug);
                        return meta ? { ...meta, views: row.views } : null;
                    })
                    .filter(Boolean)
                    .slice(0, 6); // Take top 6 AFTER filtering out unknown/legacy slugs
                setItems(enriched);
                setLoaded(true);
            })
            .catch(() => {
                if (!cancelled) setLoaded(true);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    if (!loaded || items.length === 0) return null;

    return (
        <section className="container-editorial py-12" data-testid="popular-tools-section">
            <div className="flex items-end justify-between gap-6 mb-6 border-b border-border pb-4">
                <div>
                    <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-accent">
                        <TrendingUp className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden="true" />
                        Trending now
                    </div>
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-primary mt-1">
                        What other readers are running
                    </h2>
                </div>
                <p className="hidden md:block max-w-sm text-sm text-muted-foreground text-right">
                    Live from the past few days. The most-clicked calculators on the site right now.
                </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {items.map((t, i) => (
                    <Link
                        key={t.slug}
                        to={`/calculator/${t.slug}`}
                        data-testid={`popular-tool-${t.slug}`}
                        className="group bg-card border border-border rounded-lg p-4 hover:border-primary/40 hover:shadow-[0_8px_24px_rgba(10,35,24,0.06)] transition-all relative"
                    >
                        <div className="font-mono tabular text-[10px] text-muted-foreground mb-1">
                            #{String(i + 1).padStart(2, "0")}
                        </div>
                        <div className="font-serif text-base text-primary leading-tight pr-4">
                            {t.short}
                        </div>
                        <ArrowRight className="absolute top-4 right-4 w-3.5 h-3.5 text-accent opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.75} />
                    </Link>
                ))}
            </div>
        </section>
    );
}
